/**
 * ReportSummary.js — Executive stats + AI narrative
 */
import React from "react";
import { SEVERITY_COLORS, formatNumber } from "../reportUtils";

export default function ReportSummary({
  stats, mlResult, riskScore, riskLabel, mttd, alertFatigue,
  aiNarrative, aiImpact, aiLoading, aiError, theme, onGenerateAI
}) {
  if (!stats) return null;
  const natureBlue = "#6CA2AF";
  const metricLabelColor = natureBlue;
  const metricValueColor = riskLabel?.color || theme.colors.danger;
  const metric = (label, value) => (
    <span style={{ color: metricLabelColor }}>
      {label}: <strong style={{ color: metricValueColor }}>{value}</strong>
    </span>
  );

  return (
    <div className="report-section" style={{ marginBottom: 24 }}>
      <SectionTitle color={natureBlue}>Executive Summary</SectionTitle>

      {/* Stat tiles */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        {[
          { label: "TOTAL EVENTS",  value: formatNumber(stats.total),    color: theme.colors.accent                },
          { label: "CRITICAL",      value: formatNumber(stats.critical),  color: SEVERITY_COLORS.critical },
          { label: "HIGH",          value: formatNumber(stats.high),      color: SEVERITY_COLORS.high     },
          { label: "MEDIUM",        value: formatNumber(stats.medium),    color: SEVERITY_COLORS.medium   },
          { label: "LOW",           value: formatNumber(stats.low),       color: SEVERITY_COLORS.low      },
          { label: "ATTACK TYPES",  value: stats.attackDist.length,       color: theme.colors.accent                },
          { label: "RISK SCORE",    value: `${riskScore}/100`,            color: riskLabel.color          },
          { label: "MTTD EST.",     value: mttd,                          color: "#00695C"                },
          { label: "ALERT FATIGUE", value: `${alertFatigue}%`,            color: SEVERITY_COLORS.medium   },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: `${color}12`, border: `1px solid ${color}33`,
            borderRadius: 8, padding: "12px 18px", textAlign: "center", flex: 1, minWidth: 100
          }}>
            <div style={{ fontSize: 22, fontWeight: 900, color, fontFamily: "monospace" }}>{value}</div>
            <div style={{ fontSize: 10, color: theme.colors.textMuted, letterSpacing: 1, marginTop: 2, fontFamily: "monospace" }}>{label}</div>
          </div>
        ))}
      </div>

      {/* ML Results strip */}
      {mlResult && (
        <div style={{
          background: "rgba(0,230,118,0.06)", border: "1px solid rgba(0,230,118,0.25)",
          borderRadius: 8, padding: "12px 18px", marginBottom: 20,
          fontFamily: "monospace", fontSize: 12, display: "flex", gap: 32, flexWrap: "wrap"
        }}>
          {metric("ML MODEL", (mlResult.model || "").replace(/_/g," ").toUpperCase())}
          {metric("Anomalies", `${formatNumber(mlResult.anomalies)} / ${formatNumber(mlResult.total_records)}`)}
          {metric("Rate", `${mlResult.anomaly_rate}%`)}
          {mlResult.metrics && <>
            {metric("Accuracy", `${mlResult.metrics.accuracy}%`)}
            {metric("F1", `${mlResult.metrics.f1}%`)}
          </>}
          {mlResult.vendor && mlResult.vendor !== "generic" && (
            metric("Vendor", mlResult.vendor.replace(/_/g," ").toUpperCase())
          )}
        </div>
      )}

      {/* AI Narrative */}
      {(aiNarrative || aiLoading || aiError) ? (
        <div style={{
          background: `${natureBlue}10`, border: `1px solid ${natureBlue}40`,
          borderRadius: 10, padding: 24, marginBottom: 16
        }}>
          <div style={{ fontFamily: "monospace", fontSize: 11, color: natureBlue, letterSpacing: 1, fontWeight: 700, marginBottom: 14 }}>
            ✦ EXECUTIVE SUMMARY — AI GENERATED
          </div>
          {aiLoading && <div style={{ color: theme.colors.textMuted, fontFamily: "monospace", fontSize: 12 }}>◌ Generating analysis…</div>}
          {aiError   && <div style={{ color: "#D32F2F", fontFamily: "monospace", fontSize: 12 }}>{aiError}</div>}
          {aiNarrative && (
            <div style={{ fontSize: 14, lineHeight: 1.8, color: theme.colors.textSecondary }}>
              {aiNarrative.split("\n\n").map((p, i) => <p key={i} style={{ margin: "0 0 12px 0" }}>{p}</p>)}
            </div>
          )}
          {aiImpact && (
            <>
              <div style={{ fontFamily: "monospace", fontSize: 11, color: "#F57C00", letterSpacing: 1, fontWeight: 700, margin: "16px 0 10px" }}>
                ⚡ BUSINESS IMPACT
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.8, color: theme.colors.textSecondary }}>
                {aiImpact.split("\n\n").map((p, i) => <p key={i} style={{ margin: "0 0 12px 0" }}>{p}</p>)}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="no-print" style={{
          background: `${natureBlue}0A`, border: `1px dashed ${natureBlue}4D`,
          borderRadius: 10, padding: 20, textAlign: "center"
        }}>
          <div style={{ color: natureBlue, fontFamily: "monospace", fontSize: 12, marginBottom: 10 }}>
            AI-generated executive summary not yet generated
          </div>
          <button onClick={onGenerateAI} style={{
            padding: "8px 20px", background: `${natureBlue}26`,
            border: `1px solid ${natureBlue}80`, borderRadius: 6,
            color: natureBlue, fontFamily: "monospace", fontWeight: 700,
            fontSize: 11, letterSpacing: 1, cursor: "pointer"
          }}>✦ GENERATE AI NARRATIVE</button>
        </div>
      )}
    </div>
  );
}

const SectionTitle = ({ children, color = "#1565C0" }) => (
  <div style={{
    fontFamily: "monospace", fontSize: 13, fontWeight: 900,
    color, letterSpacing: 2, textTransform: "uppercase",
    borderLeft: `4px solid ${color}`, paddingLeft: 12,
    marginBottom: 16
  }}>{children}</div>
);
